import { Address, IListData, Locality } from '@models';
import { getBackDeliveriesDescriptionForTooltip, parseBackDeliveriesFromInvoice } from '@shared';
import * as moment from 'moment/moment';
import { InvoiceCargoType } from '../../../models/invoice/invoice-cargo-type.model';
import { InvoiceContact } from '../../../models/invoice/invoice-contact.model';
import { InvoicePaymentMethod } from '../../../models/invoice/invoice-payment-method.model';
import { InvoiceServiceType } from '../../../models/invoice/invoice-service-type.model';
import { Invoice, InvoiceMy } from '../../../models/invoice/invoice.model';
import { ServiceTypeParts } from '../invoice-api.enums';
import { getCounterPartyType } from '../invoice.api';
import {
  _getState,
  coerceToNumber,
  createShortDescription,
  getAddressPickupCostWithDiscount,
  getAddressPickupCostWithoutDiscount,
  getRecipientServiceType,
  getSenderServiceType,
  isInternational,
  splitName,
} from '../invoices.utils';

/* Added this to handle invoice-my registries
 * and perform checks for creating and adding registries
 * */
const RefProperties = [
  'Ref',
  'CitySender',
  'CityRecipient',
  'State',
  'SenderAddress',
  'RecipientAddress',
  'Sender',
  'ContactSender',
  'Recipient',
  'ContactRecipient',
  'Cost',
  'AnnouncedPrice',
];

export function copyProps(src: any, dist: any, props: string[]): void {
  props.forEach((prop) => (dist[prop] = src[prop] ? src[prop] : ''));
}

// Function to map INVOICES_MY list
export function bindListToModel(data: any): IListData<Invoice> {
  return {
    list: data.data.map((el: any) => {
      try {
        const invoice = new InvoiceMy(el.Ref);
        copyProps(el, invoice, RefProperties);
        invoice.SenderDescription = el.SenderDescription;
        invoice.SenderContactPerson = el.SenderContactPerson;
        invoice.CitySenderDescription = el.CitySenderDescription;
        invoice.SenderAddressDescription = el.SenderAddressDescription;
        invoice.RecipientDescription = el.RecipientDescription;
        invoice.RecipientContactPerson = el.RecipientContactPerson;
        invoice.CounterpartyRecipientDescription = el.CounterpartyRecipientDescription;
        invoice.ScanSheetNumber = el.ScanSheetNumber;
        invoice.ScanSheetRef = el.ScanSheetRef;
        invoice.estimatedDeliveryDate = el.EstimatedDeliveryDate;
        invoice.SendersPhone = el.SendersPhone;
        invoice.BackwardDeliveryDataDocuments = el.BackwardDeliveryDataDocuments;

        invoice.docNumber = el.IntDocNumber || el.Number;
        invoice.CarCallNumber = el.CarCallNumber;
        invoice.announcedPrice = parseFloat(el.Cost || el.AnnouncedPrice) || 0;
        invoice.description = el.Description;
        invoice.payer = el.PayerType;
        invoice.MarketplacePartnerToken = el.MarketplacePartnerToken;
        invoice.backDelivery = new InvoiceCargoType(null);
        invoice.BackwardDeliveryData = el.BackwardDeliveryData;

        if (el.BackwardDeliveryData) {
          invoice.backDeliveries = parseBackDeliveriesFromInvoice(el);
          invoice.backDeliveriesTooltipDescription = getBackDeliveriesDescriptionForTooltip(invoice);
        }

        if (el.BackwardDeliveryCargoType) {
          invoice.backDelivery = new InvoiceCargoType();
          invoice.backDelivery.description = el.BackwardDeliveryCargoType;
          if (el.BackwardDeliveryMoney) {
            invoice.backMoney = el.BackwardDeliveryMoney;
          }
        }

        invoice.senderAddress = new Address();

        if (el.SenderAddressDescription) {
          invoice.senderAddress.description = el.SenderAddressDescription;
          invoice.senderAddress.shortDescription = createShortDescription(
            el.SenderAddressDescription,
            getSenderServiceType(el.ServiceType) === ServiceTypeParts.WAREHOUSE,
          );
        }

        let userNameParts;
        el.RecipientContactPerson =
          el.RecipientContactPerson || el.RecipientContactPersonFullName || el.RecipientFullName;

        if (el.RecipientContactPerson) {
          userNameParts = splitName(el.RecipientContactPerson);
          invoice.recipient = new InvoiceContact(userNameParts[1], userNameParts[0], el.ContactRecipientRef);
        } else {
          invoice.recipient = new InvoiceContact('-', '-', el.ContactRecipientRef);
        }

        invoice.recipientContactPhone = el.RecipientContactPhone || '';
        invoice.recipient.city = new Locality(el.CityRecipient);
        invoice.recipient.city.name = el.CityRecipientDescription;

        invoice.recipientCounterpartyType =
          el.RecipientCounterpartyType || getCounterPartyType(el.CounterpartyRecipientDescription) || '';

        invoice.recipient.description = el.RecipientDescription;

        invoice.recipientAddress = new Address();

        if (el.RecipientAddressDescription) {
          invoice.recipientAddress.description = el.RecipientAddressDescription;
          invoice.recipientAddress.shortDescription = createShortDescription(
            el.RecipientAddressDescription,
            getRecipientServiceType(el.ServiceType) === ServiceTypeParts.WAREHOUSE,
          );
        }

        userNameParts = splitName(el.SenderContactPerson);
        invoice.sender = new InvoiceContact(userNameParts[1], userNameParts[0], el.ContactSenderRef);
        invoice.sender.city = new Locality(el.CitySender);
        invoice.sender.city.name = el.CitySenderDescription;
        invoice.sender.description = el.SenderDescription;

        invoice.isPrinted = el.Printed === '1' || el.Printed === true;

        invoice.state = _getState(el);
        invoice.serviceType = new InvoiceServiceType(el.ServiceType);
        invoice.cargoType = new InvoiceCargoType(el.CargoType);
        invoice.createTime = moment(el.CreateTime).toDate();
        invoice.dateTime = moment(el.DateTime).toDate();
        invoice.deliveryPrice = parseFloat(el.CostOnSite || el.DocumentCost) || 0;
        invoice.StoragePrice = el.StoragePrice ? el.StoragePrice : 0;
        invoice.SumBeforeCheckWeight =
          parseFloat(el.SumBeforeCheckWeight) > 0 ? parseFloat(el.SumBeforeCheckWeight) : invoice.deliveryPrice;
        invoice.paymentControl = coerceToNumber(el.AfterpaymentOnGoodsCost);
        invoice.forwardingCount =
          !!el.ForwardingCount && parseFloat(el.ForwardingCount) > 0 ? parseFloat(el.ForwardingCount) : 0;
        invoice.ForwardingCount =
          !!el.ForwardingCount && parseFloat(el.ForwardingCount) > 0 ? parseFloat(el.ForwardingCount) : 0;
        invoice.paymentMethod = new InvoicePaymentMethod(el.PaymentMethod);
        invoice.weight = el.Weight;
        invoice.seatsAmount = el.SeatsAmount;
        invoice.infoRegClientBarcodes = el.InfoRegClientBarcodes;

        if (el.bCardPaymentCancelled !== undefined) {
          invoice.isPayCancel = el.bCardPaymentCancelled;
        }

        if (el.bCardPaymentPayed !== undefined) {
          invoice.isPayPaid = el.bCardPaymentPayed;
        }

        invoice.marketPlaceToken = el.MarketplacePartnerToken;
        invoice.isInternational = isInternational(el);
        invoice.registerDocNumber = el.ScanSheetNumber ? el.ScanSheetNumber : '';
        invoice.isRegisterPrinted = el.ScanSheetPrinted === '1';
        invoice.MarketplacePartnerDescription = el.MarketplacePartnerDescription;
        invoice.RedeliveryPaymentCard = el.RedeliveryPaymentCard
          ? el.RedeliveryPaymentCard
          : {
            CardDescription: '',
            CardMaskedNumber: '',
          };
        invoice.estimatedDeliveryDate = moment(el.EstimatedDeliveryDate).toDate();
        invoice.PackingNumber = el.PackingNumber ?? '';
        invoice.AccompanyingDocuments = el.AccompanyingDocuments ?? '';
        invoice.AdditionalInformation = el.AdditionalInformation ?? '';
        invoice.RegionCode = el.RegionCode ?? '';
        invoice.RedeliveryPaymentCard.CardMaskedNumber = el.CardMaskedNumber;
        invoice.LightReturn = el.LightReturn;
        invoice.possibilityCreateLightReturn = el.possibilityCreateLightReturn;
        invoice.LightReturnNumber = el.LightReturnNumber;
        invoice.DeliveryPharma = el.DeliveryPharma;
        invoice.CanCourierCall = el.CanCourierCall;
        invoice.PickupNumber = el.PickupNumber;
        invoice.IntDocNumber = el.IntDocNumber;
        invoice.DeliveryFrom = el.DeliveryFrom;
        invoice.addressPickupCostWithoutDiscount = getAddressPickupCostWithoutDiscount(el);
        invoice.addressPickupCostWithDiscount = getAddressPickupCostWithDiscount(el);
        invoice.OptionsSeat = el.OptionsSeat;

        return invoice;
      } catch (e) {
        console.log('Error in bindListToModel', e);
      }
    }),
    total: data.info?.totalCount ?? data?.data?.length,
  };
}
